import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  History, Eye, Save, Loader2, FileText, Mail, MessageSquare, Undo2, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-220a — Admin IDV Rejection Template Manager.
 *
 * Manages the `idv_rejection_templates` catalogue used by the IDV reject
 * modal. Admins can edit email + SMS bodies, internal notes, and re-upload
 * instructions per canonical reason_key. Version history is preserved on
 * every save (rollback available). Preview renders the final body with
 * variable substitution before any talent receives it.
 *
 * Tokens: {first_name} {rejection_reason} {upload_link}
 *         {support_email} {resubmission_deadline} {admin_notes}
 */

const TOKEN_REF = [
  '{first_name}', '{rejection_reason}', '{upload_link}',
  '{support_email}', '{resubmission_deadline}', '{admin_notes}',
];

const IdvRejectionTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTmpl, setNewTmpl] = useState({
    reason_key: '', label: '', email_subject: '', email_body: '', sms_body: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/idv-rejection-templates');
      setTemplates(r.data?.items || []);
      if (!selectedId && (r.data?.items || []).length) {
        setSelectedId(r.data.items[0].id);
      }
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    const t = templates.find(x => x.id === selectedId);
    setDraft(t ? { ...t } : null);
    setPreview(null);
  }, [selectedId, templates]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/idv-rejection-templates/${draft.id}`, {
        label: draft.label,
        email_subject: draft.email_subject,
        email_body: draft.email_body,
        sms_body: draft.sms_body,
        internal_notes: draft.internal_notes,
        reupload_instructions: draft.reupload_instructions,
        is_active: draft.is_active,
        change_reason: draft._change_reason || 'admin edit',
      });
      toast.success(`Saved as v${r.data.version}`);
      // Refresh list with updated row.
      setTemplates(prev => prev.map(t => t.id === draft.id ? { ...r.data } : t));
      setDraft({ ...r.data, _change_reason: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!draft) return;
    try {
      const r = await api.post(`/admin/idv-rejection-templates/${draft.id}/preview`, {
        admin_notes: 'Sample admin note for preview.',
      });
      setPreview(r.data);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const openVersions = async () => {
    if (!draft) return;
    try {
      const r = await api.get(`/admin/idv-rejection-templates/${draft.id}/versions`);
      setVersions(r.data?.versions || []);
      setVersionsOpen(true);
    } catch (e) {
      toast.error('Failed to load versions');
    }
  };

  const restore = async (v) => {
    if (!draft) return;
    try {
      const r = await api.post(`/admin/idv-rejection-templates/${draft.id}/restore/${v.version}`);
      toast.success(`Restored v${v.version} as v${r.data.version}`);
      setVersionsOpen(false);
      setTemplates(prev => prev.map(t => t.id === draft.id ? { ...r.data } : t));
      setDraft({ ...r.data, _change_reason: '' });
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const submitNew = async () => {
    try {
      const r = await api.post('/admin/idv-rejection-templates', {
        reason_key: newTmpl.reason_key,
        label: newTmpl.label,
        email_subject: newTmpl.email_subject || 'Re-upload needed for your iEvntür ID',
        email_body: newTmpl.email_body || 'Hi {first_name}, please re-upload your ID at {upload_link}.',
        sms_body: newTmpl.sms_body || 'iEvntür: Please re-upload your ID at {upload_link}.',
        is_active: true,
      });
      toast.success(`Created template "${r.data.label}"`);
      setCreateOpen(false);
      setNewTmpl({ reason_key: '', label: '', email_subject: '', email_body: '', sms_body: '' });
      load();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-8 overflow-x-hidden" data-testid="idv-rejection-templates-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-cyan-600" /> IDV Rejection Templates
            </h1>
            <p className="text-sm text-gray-500">Admin-editable email + SMS templates for each rejection reason. Versions auto-archived on every save.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="idv-template-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New reason
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* List */}
          <Card className="bg-white">
            <CardContent className="p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Reasons ({templates.length})</p>
              {loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <ul className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="idv-template-list">
                  {templates.map(t => (
                    <li key={t.id}>
                      <button
                        onClick={() => setSelectedId(t.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedId === t.id ? 'bg-cyan-50 text-cyan-900 border border-cyan-200' : 'hover:bg-gray-50 border border-transparent'}`}
                        data-testid={`idv-template-row-${t.reason_key}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{t.label}</span>
                          <Badge variant="outline" className="text-[10px] shrink-0">v{t.version}</Badge>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <code className="text-[10px] text-gray-500">{t.reason_key}</code>
                          {t.is_default && <Badge className="bg-amber-100 text-amber-700 text-[10px] hover:bg-amber-100">fallback</Badge>}
                          {!t.is_active && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Editor */}
          <Card className="bg-white lg:col-span-2">
            <CardContent className="p-4">
              {!draft ? (
                <p className="text-sm text-gray-500 py-10 text-center">Select a template to edit</p>
              ) : (
                <div className="space-y-3" data-testid="idv-template-editor">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{draft.label}</h2>
                      <code className="text-xs text-gray-500">{draft.reason_key} · v{draft.version}</code>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={runPreview} data-testid="idv-template-preview-btn">
                        <Eye className="w-4 h-4 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={openVersions} data-testid="idv-template-versions-btn">
                        <History className="w-4 h-4 mr-1" /> History
                      </Button>
                      <Button size="sm" onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="idv-template-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Save
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Label (admin-facing)</Label>
                    <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="idv-template-label" />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email subject
                    </Label>
                    <Input value={draft.email_subject} onChange={(e) => setDraft({ ...draft, email_subject: e.target.value })} data-testid="idv-template-email-subject" />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email body
                    </Label>
                    <Textarea rows={5} value={draft.email_body} onChange={(e) => setDraft({ ...draft, email_body: e.target.value })} data-testid="idv-template-email-body" />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> SMS body
                    </Label>
                    <Textarea rows={2} value={draft.sms_body} onChange={(e) => setDraft({ ...draft, sms_body: e.target.value })} data-testid="idv-template-sms-body" />
                    <p className="text-[11px] text-gray-500 mt-1">~160 chars per segment. Current: {(draft.sms_body || '').length}</p>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Internal admin notes</Label>
                    <Textarea rows={2} value={draft.internal_notes || ''} onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })} placeholder="Hint shown to admins on the reject modal" />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Re-upload instructions (talent-facing)</Label>
                    <Textarea rows={2} value={draft.reupload_instructions || ''} onChange={(e) => setDraft({ ...draft, reupload_instructions: e.target.value })} />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Change reason (audit)</Label>
                    <Input value={draft._change_reason || ''} onChange={(e) => setDraft({ ...draft, _change_reason: e.target.value })} placeholder="Optional — why are you editing this?" />
                  </div>

                  <details className="text-xs text-gray-600">
                    <summary className="cursor-pointer font-medium">Available tokens</summary>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {TOKEN_REF.map(t => (
                        <code key={t} className="bg-gray-100 px-2 py-0.5 rounded text-[11px]">{t}</code>
                      ))}
                    </div>
                  </details>

                  {preview && (
                    <Card className="bg-gray-50 border-cyan-200" data-testid="idv-template-preview-card">
                      <CardContent className="p-3 space-y-2">
                        <p className="text-xs uppercase tracking-wide text-cyan-700 font-medium">Preview (rendered)</p>
                        <div>
                          <p className="text-[11px] text-gray-500">Email subject</p>
                          <p className="text-sm font-medium">{preview.email_subject}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Email body</p>
                          <pre className="text-sm bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words">{preview.email_body}</pre>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">SMS body</p>
                          <pre className="text-sm bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words">{preview.sms_body}</pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Versions dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="idv-template-versions-dialog">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>Every save is archived. You can restore any prior version.</DialogDescription>
          </DialogHeader>
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No prior versions yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {versions.map(v => (
                <li key={v.id} className="py-3 flex items-start justify-between gap-3" data-testid={`idv-template-version-row-${v.version}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">v{v.version}</p>
                    <p className="text-xs text-gray-500">
                      {v.changed_by_email || 'system'} · {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                    </p>
                    {v.change_reason && <p className="text-xs text-gray-700 mt-1 italic">"{v.change_reason}"</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(v)} data-testid={`idv-template-restore-${v.version}`}>
                    <Undo2 className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="idv-template-create-dialog">
          <DialogHeader>
            <DialogTitle>New rejection template</DialogTitle>
            <DialogDescription>Adds a new admin-pickable reason to the IDV reject modal dropdown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">Reason key (lowercase, snake_case)</Label>
              <Input value={newTmpl.reason_key} onChange={(e) => setNewTmpl({ ...newTmpl, reason_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="e.g. military_id_unsupported" data-testid="idv-template-new-key" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">Label (admin-facing)</Label>
              <Input value={newTmpl.label} onChange={(e) => setNewTmpl({ ...newTmpl, label: e.target.value })} data-testid="idv-template-new-label" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">Email subject (optional default applied if blank)</Label>
              <Input value={newTmpl.email_subject} onChange={(e) => setNewTmpl({ ...newTmpl, email_subject: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">SMS body (optional default applied if blank)</Label>
              <Textarea rows={2} value={newTmpl.sms_body} onChange={(e) => setNewTmpl({ ...newTmpl, sms_body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitNew} disabled={!newTmpl.reason_key || !newTmpl.label} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="idv-template-new-submit">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdvRejectionTemplates;
